import React from "react";
import { ArtworkStub } from "../../models/Artwork";

interface Props {
    artwork: ArtworkStub;
    position: { x: number; y: number };
    currency: string;
    showValues: boolean;
    convertedValue?: number;
}

const formatShortDate = (value?: string) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatCreationDate = (value?: string) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.getFullYear().toString();
};

const HoverMetaRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
    if (!value) return null;

    return (
        <div className="hover-preview-meta-row">
            <span className="hover-preview-meta-label">{label}</span>
            <span className="hover-preview-meta-value">{value}</span>
        </div>
    );
};

const ArtworkHoverPreview: React.FC<Props> = ({
    artwork,
    position,
    currency,
    showValues,
    convertedValue,
}) => {
    const acquiredDate = formatShortDate(artwork.dateAcquired);
    const creationDate = formatCreationDate(artwork.artworkCreationDate);
    const hasEdition =
        (artwork.editionVolume ?? 0) > 0 && (artwork.editionNumber ?? 0) > 0;

    const hasImage = !!artwork.imageUrl;

    return (
        <div
            className="art-hover-preview elegant"
            style={{
                left: position.x,
                top: position.y,
            }}
        >
            <div className="hover-preview-image-shell">
                {hasImage ? (
                    <img
                        src={artwork.imageUrl}
                        alt={artwork.artWorkTitle}
                        className="hover-preview-image"
                    />
                ) : (
                    <div className="hover-preview-no-image">
                        <div className="hover-preview-no-image-icon">🖼️</div>
                        <div className="hover-preview-no-image-text">No image available</div>
                    </div>
                )}
            </div>

            <div className="hover-preview-body">
                <div className="hover-preview-artist">
                    {artwork.artistName}
                </div>

                <div className="hover-preview-title">
                    {artwork.artWorkTitle || "Untitled"}
                </div>

                <div className="hover-preview-meta">
                    <HoverMetaRow label="Year" value={creationDate} />

                    {hasEdition && (
                        <HoverMetaRow
                            label="Edition"
                            value={`${artwork.editionNumber} / ${artwork.editionVolume}`}
                        />
                    )}

                    <HoverMetaRow label="Medium" value={artwork.medium} />
                    <HoverMetaRow label="Category" value={artwork.categoryName} />
                    <HoverMetaRow label="Location" value={artwork.locationPath} />
                    <HoverMetaRow label="Acquired" value={acquiredDate} />
                    <HoverMetaRow
                        label="Images"
                        value={artwork.imageCount > 0 ? artwork.imageCount : undefined}
                    />
                </div>

                {showValues && (
                    <div className="hover-preview-valuation">
                        <div className="hover-preview-valuation-label">
                            Acquisition Value
                        </div>
                        <div className="hover-preview-valuation-primary">
                            {artwork.acquisitionValuation?.lowerRangeAmount.toLocaleString()} {artwork.acquisitionValuation?.currencyCode}
                        </div>
                        {convertedValue !== undefined && (
                            <div className="hover-preview-valuation-secondary">
                                ~ {convertedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArtworkHoverPreview;